import { type Rng, createRng } from '@precon-tools-suite-by-shared/js-utils';

/**
 * A placeholder node used instead of normal GraphQL nodes.
 */
export type PlaceholderNode = {
    __typename: 'PlaceholderNode';
    __loading: true;
    index: number;
    rng: Rng;
};

/**
 * Checks if the provided value is a placeholder node.
 *
 * @param node
 */
export function isPlaceholderNode(node: unknown): node is PlaceholderNode {
    return (
        typeof node === 'object' &&
        !!node &&
        '__typename' in node &&
        node.__typename === 'PlaceholderNode'
    );
}

/**
 * Creates a new placeholder node.
 *
 * The rng of the placeholder is determined by the `index` and the `rngSalt`.
 *
 * @param index The index of the placeholder node.
 * @param rngSalt A salt value to influence the rng.
 */
export function createPlaceholderNode(
    index: number,
    ...rngSalt: unknown[]
): PlaceholderNode {
    return {
        __typename: 'PlaceholderNode',
        __loading: true,
        index,
        rng: createRng(index, ...rngSalt),
    };
}

/**
 * A placeholder edge used instead of a normal GraphQL edge.
 */
export interface PlaceholderEdge {
    __typename: 'PlaceholderEdge';
    __loading: true;
    node: PlaceholderNode;
}

/**
 * Checks if the provided value is a placeholder edge.
 *
 * @param edge
 */
export function isPlaceholderEdge(edge: unknown): edge is PlaceholderEdge {
    return (
        typeof edge === 'object' &&
        !!edge &&
        '__typename' in edge &&
        edge.__typename === 'PlaceholderEdge'
    );
}

/**
 * Creates a new placeholder edge.
 *
 * The rng of the placeholder is determined by the `index` and the `rngSalt`.
 *
 * @param index The index of the placeholder edge.
 * @param rngSalt A salt value to influence the rng.
 */
export function createPlaceholderEdge(
    index: number,
    ...rngSalt: unknown[]
): PlaceholderEdge {
    return {
        __typename: 'PlaceholderEdge',
        __loading: true,
        node: createPlaceholderNode(index, ...rngSalt),
    };
}

/**
 * A placeholder object used instead of a normal GraphQL object.
 */
export type Placeholder = PlaceholderNode | PlaceholderEdge;

/**
 * Checks if the provided value is an placeholder object.
 *
 * @param value
 */
export function isPlaceholder(value: unknown): value is Placeholder {
    return isPlaceholderNode(value) || isPlaceholderEdge(value);
}

/**
 * Creates a new list of nodes with placeholders for the nodes that are
 * still missing.
 */
export function getNodesWithPlaceholders<N>(
    nodes: N[],
    expectedTotalCount: number | null | undefined,
    backwards = false,
    rngSalt?: unknown
): (N | PlaceholderNode)[] {
    const length = expectedTotalCount ?? null;
    if (length === null || length <= nodes.length) return nodes;

    if (backwards) {
        return Array.from({ length }).map((_, ix) => {
            if (ix < length - nodes.length) {
                return createPlaceholderNode(ix, rngSalt);
            } else {
                return nodes[ix - (length - nodes.length)];
            }
        });
    } else {
        return Array.from({ length }).map((_, ix) => {
            if (ix < nodes.length) {
                return nodes[ix];
            } else {
                return createPlaceholderNode(ix, rngSalt);
            }
        });
    }
}

/**
 * Creates a new list of edges with placeholder edges for the edges that
 * are still missing.
 */
export function getEdgesWithPlaceholders<E>(
    edges: E[],
    expectedTotalCount: number | null | undefined,
    backwards = false,
    rngSalt?: unknown
): (E | PlaceholderEdge)[] {
    const length = expectedTotalCount ?? null;
    if (length === null || length <= edges.length) return edges;

    if (backwards) {
        return Array.from({ length }).map((_, ix) => {
            if (ix < length - edges.length) {
                return createPlaceholderEdge(ix, rngSalt);
            } else {
                return edges[ix - (length - edges.length)];
            }
        });
    } else {
        return Array.from({ length }).map((_, ix) => {
            if (ix < edges.length) {
                return edges[ix];
            } else {
                return createPlaceholderEdge(ix, rngSalt);
            }
        });
    }
}
